import React, { useState, useEffect, useRef } from 'react';
import { Plus, Search, FileText, Download, Trash2, ShieldAlert } from 'lucide-react';
import toast from 'react-hot-toast';
import { confirmAction } from '../../utils/confirmToast';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import Drawer from '../../components/common/Drawer/Drawer';
import SearchableSelect from '../../components/common/SearchableSelect';
import api from '../../api/axios';
import styles from './Documents.module.css';

const Documents = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [docFile, setDocFile] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/community/documents');
      setDocuments(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const [formData, setFormData] = useState({ title: '', category: 'Legal' });

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const getFileUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    const normalized = path.replace(/\\/g, '/');
    const baseUrl = import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '');
    if (normalized.startsWith('/uploads')) {
        return `${baseUrl}/api${normalized}`;
    }
    return `${baseUrl}${normalized.startsWith('/') ? '' : '/'}${normalized}`;
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setDocFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!docFile) {
      toast.error('Please select a document file to upload.');
      return;
    }

    try {
      const uploadData = new FormData();
      uploadData.append('image', docFile); // Using 'image' field name because multer is configured for upload.single('image')

      const uploadRes = await api.post('/upload', uploadData);

      const fileUrl = uploadRes.data; // Save relative URL in backend
      const fileSizeMB = (docFile.size / (1024 * 1024)).toFixed(1) + ' MB';

      const submitData = {
        ...formData,
        size: fileSizeMB,
        filePath: fileUrl
      };

      const { data } = await api.post('/community/documents', submitData);
      setDocuments([data, ...documents]);
      setIsDrawerOpen(false);
      setFormData({ title: '', category: 'Legal' });
      setDocFile(null);
      toast.success('Document uploaded successfully');
    } catch (err) {
      toast.error('Failed to upload document');
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    confirmAction("Are you sure you want to delete this document?", async () => {
      try {
        await api.delete(`/community/documents/${id}`);
        setDocuments(documents.filter(d => d._id !== id));
        toast.success('Document deleted successfully');
      } catch (err) {
        toast.error('Failed to delete document');
      }
    });
  };

  return (
    <div className={styles.container}>
      <PageHeader 
        title="Society Documents" 
        breadcrumb={['Resources', 'Documents']} 
        actionButton={
          <button className={styles.addBtn} onClick={() => setIsDrawerOpen(true)}>
            <Plus size={16} /> Upload Document
          </button>
        }
      />

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.searchBox}>
            <Search size={16} className={styles.searchIcon} />
            <input type="text" placeholder="Search documents..." />
          </div>
        </div>

        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Document Name</th>
                <th>Category</th>
                <th>Size</th>
                <th>Uploaded On</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="5" style={{textAlign: 'center'}}>Loading documents...</td></tr> : documents.map(doc => (
                <tr key={doc._id}>
                  <td>
                    <div className={styles.docName}>
                      <FileText size={18} className={styles.iconDoc} />
                      <strong>{doc.title}</strong>
                    </div>
                  </td>
                  <td><span className={styles.badgeCategory}>{doc.category}</span></td>
                  <td>{doc.size}</td>
                  <td>{new Date(doc.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                  <td>
                    <div className={styles.actionBtns}>
                      <a href={getFileUrl(doc.filePath)} target="_blank" rel="noopener noreferrer" className={styles.btnIcon} title="Download" aria-label="Download Document"><Download size={14} /></a>
                      <button className={`${styles.btnIcon} ${styles.danger}`} title="Delete" aria-label="Delete Document" onClick={() => handleDelete(doc._id)}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={styles.mobileList}>
          {loading ? <p style={{padding: '16px', textAlign: 'center'}}>Loading documents...</p> : documents.map(doc => (
            <div key={doc._id} className={styles.mobileCard}>
              <div className={styles.cardTop}>
                <div className={styles.docName}>
                  <FileText size={18} className={styles.iconDoc} />
                  <strong>{doc.title}</strong>
                </div>
              </div>
              
              <div className={styles.cardMid}>
                <span className={styles.badgeCategory}>{doc.category}</span>
                <span style={{ fontSize: '13px', color: '#64748b' }}>Size: {doc.size}</span>
                <span style={{ fontSize: '13px', color: '#64748b' }}>
                  Uploaded: {new Date(doc.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
              </div>

              <div className={styles.cardBottom}>
                <div className={styles.actionBtns}>
                  <a href={doc.filePath} target="_blank" rel="noopener noreferrer" className={styles.btnIcon} style={{flex: 1}} title="Download">
                    <Download size={16} />
                  </a>
                  <button className={`${styles.btnIcon} ${styles.danger}`} style={{flex: 1}} title="Delete" onClick={() => handleDelete(doc._id)}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Drawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} title="Upload Document">
        <form className="drawerForm" onSubmit={handleSubmit}>
          <div className={styles.uploadArea} onClick={() => fileInputRef.current.click()} style={{cursor: 'pointer'}}>
            {docFile ? (
              <p style={{color: '#10b981', fontWeight: 500}}>Selected: {docFile.name}</p>
            ) : (
              <>
                <FileText size={32} color="#9ca3af" />
                <p>Click to select PDF or Document file</p>
                <small>Max file size: 10MB</small>
              </>
            )}
            <input 
              type="file" 
              ref={fileInputRef} 
              style={{display: 'none'}} 
              accept=".pdf,.doc,.docx" 
              onChange={handleFileChange} 
            />
          </div>

          <div className="formGroup">
            <label>Document Title</label>
            <input name="title" value={formData.title} onChange={handleChange} required placeholder="e.g. Audit Report" />
          </div>
          <div className="formGroup">
            <label>Category</label>
            <SearchableSelect name="category" value={formData.category} onChange={handleChange} id="category-select">
              <option value="Legal">Legal & By-laws</option>
              <option value="Financial">Financial Reports</option>
              <option value="Safety">Safety & Compliance</option>
              <option value="Meeting">Minutes of Meeting</option>
              <option value="General">General Forms</option>
            </SearchableSelect>
          </div>
          
          <div className={styles.alertBox}>
            <ShieldAlert size={20} className={styles.alertIcon} />
            <p>Uploaded documents will be visible to all registered residents.</p>
          </div>

          <div className="formActions">
            <button type="button" className="btnCancel" onClick={() => setIsDrawerOpen(false)}>Cancel</button>
            <button type="submit" className="btnSubmit">Upload File</button>
          </div>
        </form>
      </Drawer>
    </div>
  );
};

export default Documents;
